import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { formatAppointmentDateTime } from "../utils/dateUtils";
import { RATE_TYPE } from "../common/Constant";

const invoiceBaseUrl = process.env.REACT_APP_CASE_URL ?? "";
const invoicePort = api(invoiceBaseUrl);

interface ApiResponse<T> {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  result: T;
  timestamp: string;
}

export interface InvoiceSummaryByClientPayload {
  yearMonth: string;
  clientId: number | null;
  searchText: string;
}

export interface InvoiceSummaryByClient {
  clientId: number;
  clientName: string;
  countOfQmeDoctors: number;
  grandTotalNoOfPages: number;
  grandTotalNoOfLines: number;
  grandTotalAmount: number;
}

export interface InvoiceSummaryByClientQme {
  clientId: number;
  clientName: string;
  qmeDoctorContactId?: number;
  qmeDoctorName?: string;
  billingForUserRole?: number;
  userId?: number;
  userInformation?: string;
  countOfCases: number;
  grandTotalNoOfPages: number;
  grandTotalNoOfLines: number;
  grandTotalAmount: number;
}

export interface InvoiceByClientQmeCasePayload {
  yearMonth: string;
  invoiceLineItemStatusIds: string;
  teamId: number | null;
  clientId: number | null;
  qmeDoctorContactId?: number;
  billingForUserRole?: string;
  userId?: number;
  searchText: string;
}

export const getInvoiceReportSummary = async (payload: any): Promise<any> => {
  try {
    const { data } = await invoicePort.post<ApiResponse<any>>(
      "/api/invoice/report/outcome-summary/client",
      payload,
    );
    return data.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportByClientQme = async (
  payload: any,
): Promise<any[]> => {
  try {
    const { data } = await invoicePort.post<ApiResponse<any[]>>(
      "/api/invoice/report/outcome-summary/client-qme",
      payload,
    );
    return data.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportByClientQmeCase = async (payload: any) => {
  try {
    const { data } = await invoicePort.post(
      "/api/invoice/report/outcome-cases/client-qme",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      patientName: user.subjectInfo || "",
      taskName: user.documentTask || "",
      noOfPages: user.totalNoOfPages?.toString() || "0",
      noOfLines: user.totalNoOfLines?.toString() || "0",
      dateUploaded: user.uploadInformation || "",
      rate:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.billingRuleRate ?? 0)?.toFixed(2)}`,
      amount:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.totalAmount ?? 0)?.toFixed(2)}`,
      invoiceStatus: user.invoiceLineItemStatus || "",
      dateOfAppointmentFormat: formatAppointmentDateTime(
        user.dateOfAppointment,
      ),
      rateType: user.billingRuleRateType,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportByClientRole = async (
  payload: any,
): Promise<any[]> => {
  try {
    const { data } = await invoicePort.post<ApiResponse<any[]>>(
      "/api/invoice/report/outcome-summary/client-user-role",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      qmeDoctorName: user.billingForUserRole || "",
      qmeDoctorContactId: user.billingForUserRole || "",
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportByClientRoleCase = async (
  payload: InvoiceByClientQmeCasePayload,
) => {
  try {
    const { data } = await invoicePort.post(
      "/api/invoice/report/outcome-cases/client-user-role",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      patientName: user.subjectInfo || "",
      taskName: user.qmeDoctorName || "",
      noOfPages: user.totalNoOfPages?.toString() || "0",
      noOfLines: user.totalNoOfLines?.toString() || "0",
      dateUploaded: user.uploadInformation || "",
      rate:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.billingRuleRate ?? 0)?.toFixed(2)}`,
      amount:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.totalAmount ?? 0)?.toFixed(2)}`,
      invoiceStatus: user.invoiceLineItemStatus || "",
      dateOfAppointmentFormat: formatAppointmentDateTime(
        user.dateOfAppointment,
      ),
      rateType: user.billingRuleRateType,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportByClientUser = async (
  payload: any,
): Promise<any[]> => {
  try {
    const { data } = await invoicePort.post<ApiResponse<any[]>>(
      "/api/invoice/report/outcome-summary/client-user",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      qmeDoctorName: user.userInformation || "",
      qmeDoctorContactId: user.userId,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportByClientUserCase = async (
  payload: InvoiceByClientQmeCasePayload,
) => {
  try {
    const { data } = await invoicePort.post(
      "/api/invoice/report/outcome-cases/client-user",
      payload,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((user: any) => ({
      ...user,
      patientName: user.subjectInfo || "",
      taskName: user.qmeDoctorName || "",
      noOfPages: user.totalNoOfPages?.toString() || "0",
      noOfLines: user.totalNoOfLines?.toString() || "0",
      dateUploaded: user.uploadInformation || "",
      rate:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.billingRuleRate ?? 0)?.toFixed(2)}`,
      amount:
        user.currencySymbol === "X"
          ? ""
          : `${user.currencySymbol} ${Number(user.totalAmount ?? 0)?.toFixed(2)}`,
      invoiceStatus: user.invoiceLineItemStatus || "",
      dateOfAppointmentFormat: formatAppointmentDateTime(
        user.dateOfAppointment,
      ),
      rateType: user.billingRuleRateType,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const getInvoiceReportSummaryCount = async (
  invoiceData: any,
): Promise<any> => {
  try {
    const response = await invoicePort.post<ApiResponse<any>>(
      "/api/invoice/report/outcome-summary/header",
      invoiceData,
    );

    return response.data?.result;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

export const cancelInvoice = async (invoiceData: any): Promise<any> => {
  try {
    const response = await invoicePort.post<ApiResponse<any>>(
      "/api/invoice/report/cancel",
      invoiceData,
    );

    return response.data;
  } catch (error) {
    console.error("Error cancelling a invoice:", error);
    throw new Error(
      "Failed to cancel the invoice. Please check the data and try again.",
    );
  }
};

export const cancelInvoiceLineItem = async (invoiceData: any): Promise<any> => {
  try {
    const response = await invoicePort.post<ApiResponse<any>>(
      "/api/invoice/report/line-item/cancel",
      invoiceData,
    );

    return response.data;
  } catch (error) {
    console.error("Error cancelling a invoice:", error);
    throw new Error(
      "Failed to cancel the invoice. Please check the data and try again.",
    );
  }
};

export const getInvoiceReportLogs = async (invoiceID: any): Promise<any> => {
  try {
    const response = await invoicePort.get(
      `/api/invoice/report/audit-log?invoiceId=${invoiceID}`,
    );
    return response.data?.result ?? [];
  } catch (error) {
    console.error("Error getting logs:", error);
    throw new Error(
      "Failed to get the logs. Please check the data and try again.",
    );
  }
};
